import { useParams } from 'react-router-dom';
import {
  Check,
  ArrowUpRight,
  ArrowLeft,
  Users,
  Shield,
  DollarSign,
  X,
  Zap,
  MessageSquare,
  BarChart3,
  MousePointerClick,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { integrations } from '@/data/integrations';

const extraFacts: Record<string, { label: string; value: string; source: string }[]> = {
  whatsapp: [
    { label: 'Monthly active users', value: '2+ billion', source: 'Meta / Business of Apps' },
    { label: 'Daily messages sent', value: '100+ billion', source: 'Meta investor materials' },
    { label: 'Average open rate', value: '90%+', source: 'Industry benchmarks' },
  ],
  instagram: [
    { label: 'Monthly active users', value: '2+ billion', source: 'Meta / Statista' },
    { label: 'Users who discover brands', value: '90%', source: 'Meta for Business' },
    { label: 'Users who take action', value: '70%', source: 'Instagram for Business' },
  ],
  telegram: [
    { label: 'Monthly active users', value: '800+ million', source: 'Telegram' },
    { label: 'Messages delivered daily', value: 'Billions', source: 'Telegram' },
    { label: 'Group chat capacity', value: '200,000', source: 'Telegram' },
  ],
  messenger: [
    { label: 'Monthly active users', value: '1+ billion', source: 'Meta' },
    { label: 'Businesses on Messenger', value: '40+ million', source: 'Meta for Business' },
    { label: 'Average open rate', value: '75%+', source: 'Industry benchmarks' },
  ],
  'google-meet': [
    { label: 'Workspace users', value: '3+ billion', source: 'Google Workspace' },
    { label: 'Calls secured', value: 'Encrypted by default', source: 'Google Cloud' },
    { label: 'Meeting minutes', value: 'Unlimited', source: 'Google Meet' },
  ],
  'microsoft-teams': [
    { label: 'Daily users', value: '300+ million', source: 'Microsoft' },
    { label: 'Organizations using Teams', value: 'Millions', source: 'Microsoft' },
    { label: 'Channels and chats', value: 'Enterprise-grade', source: 'Microsoft 365' },
  ],
  notion: [
    { label: 'Users worldwide', value: '30+ million', source: 'Notion' },
    { label: 'Workspaces', value: '4+ million', source: 'Notion' },
    { label: 'Community templates', value: '10,000+', source: 'Notion' },
  ],
  jira: [
    { label: 'Teams using Jira', value: '65,000+', source: 'Atlassian' },
    { label: 'Issue tracking status', value: 'Industry standard', source: 'Atlassian' },
    { label: 'Marketplace integrations', value: '3,000+', source: 'Atlassian' },
  ],
  monday: [
    { label: 'Customers', value: '150,000+', source: 'Monday.com' },
    { label: 'Industries', value: '200+', source: 'Monday.com' },
    { label: 'Templates', value: '200+', source: 'Monday.com' },
  ],
  zurichat: [
    { label: 'Availability', value: 'Global edge', source: 'Zuri Hub' },
    { label: 'Latency', value: '<100ms', source: 'Zuri Cloud' },
    { label: 'Messages', value: 'Encrypted at rest', source: 'Zuri Hub' },
  ],
  mailchimp: [
    { label: 'Users', value: '14+ million', source: 'Intuit / Mailchimp' },
    { label: 'Emails sent daily', value: 'Billions', source: 'Mailchimp' },
    { label: 'Automations', value: 'Pre-built journeys', source: 'Mailchimp' },
  ],
  kit: [
    { label: 'Audience limit', value: 'Unlimited', source: 'Kit' },
    { label: 'Creator-focused', value: 'Built for newsletters', source: 'Kit' },
    { label: 'Automations', value: 'Visual sequences', source: 'Kit' },
  ],
  intercom: [
    { label: 'Businesses served', value: '25,000+', source: 'Intercom' },
    { label: 'Conversations per month', value: 'Hundreds of millions', source: 'Intercom' },
    { label: 'Migration data', value: 'Full history import', source: 'Zuri Hub' },
  ],
  stripe: [
    { label: 'Businesses', value: 'Millions', source: 'Stripe' },
    { label: 'Countries', value: '46+', source: 'Stripe' },
    { label: 'API uptime', value: '99.99%', source: 'Stripe' },
  ],
  paypal: [
    { label: 'Active accounts', value: '400+ million', source: 'PayPal' },
    { label: 'Markets', value: '200+', source: 'PayPal' },
    { label: 'Buyer coverage', value: 'Global', source: 'PayPal' },
  ],
  docusign: [
    { label: 'Customers', value: '1+ million', source: 'DocuSign' },
    { label: 'Users', value: '1+ billion', source: 'DocuSign' },
    { label: 'Documents signed', value: '1+ billion', source: 'DocuSign' },
  ],
  calendly: [
    { label: 'Users', value: '20+ million', source: 'Calendly' },
    { label: 'Meetings scheduled', value: '100+ million', source: 'Calendly' },
    { label: 'Integrations', value: '100+', source: 'Calendly' },
  ],
  wordpress: [
    { label: 'Websites powered', value: '40%+', source: 'WordPress' },
    { label: 'Plugins', value: '50,000+', source: 'WordPress.org' },
    { label: 'Open source', value: 'Yes', source: 'WordPress Foundation' },
  ],
};

const whyZuri = [
  { title: 'One inbox for everything', text: 'No more switching between apps and tabs.' },
  { title: 'AI that learns your tone', text: 'Zyra AI drafts replies and keeps responses consistent.' },
  { title: 'Smart routing', text: 'Route by topic, urgency, language, or team.' },
  { title: 'Real-time reporting', text: 'See channel and agent performance in one dashboard.' },
];

const defaultFaqs = [
  { q: 'Is this integration included?', a: 'Core integrations are included in ZuriHub Pro. Some third-party APIs may require their own account or usage plan.' },
  { q: 'Do I need a separate account?', a: 'Most integrations require an existing account with the third-party provider. You connect it to ZuriHub with a few clicks.' },
  { q: 'Can I connect more than one workspace?', a: 'Yes. Pro and Enterprise plans support multiple connected accounts and workspaces.' },
];

const painPoints = [
  'Customers message you on this channel, but your team replies from another.',
  'You lose context because the conversation is not tied to the customer record.',
  'Agents waste time switching apps and copying information.',
];

function getPage(slug: string) {
  const item = integrations.find((i) => i.id === slug);
  if (!item) return null;

  const facts = extraFacts[slug] || [
    { label: 'Category', value: item.category, source: 'Zuri Hub' },
    { label: 'Integration type', value: 'Native', source: 'Zuri Hub' },
    { label: 'Setup time', value: 'Minutes', source: 'Zuri Hub' },
  ];

  const pricing = item.category.includes('Commerce') || ['stripe', 'paypal', 'docusign', 'calendly', 'mailchimp', 'intercom', 'kit', 'wordpress'].includes(slug)
    ? 'No extra fee from ZuriHub for this integration. You may need a separate account with the third-party provider. ZuriHub Pro is $25/agent and includes all core integrations.'
    : 'This channel is included in ZuriHub Pro at $25/agent. There is no additional per-message or per-integration charge.';

  return {
    item,
    name: item.name,
    title: `${item.name} + ZuriHub`,
    description: item.benefit,
    stats: facts,
    how: item.howItWorks,
    benefit: item.benefit,
    scalability: item.scalability,
    pricing,
    whyZuri,
    faqs: defaultFaqs,
    color: item.color,
  };
}

export default function Integration() {
  const { slug } = useParams<{ slug: string }>();
  const page = slug ? getPage(slug) : null;

  if (!page) {
    return (
      <section className="px-4 pt-40 pb-24 text-center">
        <h1 className="text-3xl font-bold text-slate-900">Integration not found</h1>
        <p className="mt-2 text-slate-600">Check the integrations page for supported channels.</p>
      </section>
    );
  }

  const { item, name, title, description, stats, how, benefit, scalability, pricing, whyZuri: why, faqs, color } = page;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${site.baseUrl}/integrations/${slug}`,
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const IntegrationIcon = item.Icon;

  return (
    <>
      <SEO title={title} description={description} path={`/integrations/${slug}`} jsonLd={[jsonLd, faqJsonLd]} />

      {/* Hero with brand accent */}
      <section
        className="relative overflow-hidden px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8"
        style={{ backgroundColor: color, backgroundImage: `linear-gradient(135deg, ${color} 0%, #0f172a 100%)` }}
      >
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="text-center lg:text-left">
              <Link to="/integrations" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-white/20">
                <ArrowLeft className="h-4 w-4" /> All integrations
              </Link>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                {title}
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-white/90 lg:mx-0">
                {description}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold transition hover:bg-slate-100"
                  style={{ color }}
                >
                  Connect {name} <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-base font-bold text-white transition hover:bg-white/20"
                >
                  Book a demo
                </a>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="rounded-3xl bg-white/10 p-10 shadow-2xl backdrop-blur ring-1 ring-white/20">
                <IntegrationIcon
                  name={item.iconType === 'brand' ? item.name : undefined}
                  className="h-28 w-28 text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-white py-16" aria-labelledby="stats-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-3">
            {stats.map((s, i) => (
              <div
                key={i}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <p className="text-3xl font-extrabold text-slate-900">{s.value}</p>
                <p className="mt-1 font-semibold text-slate-700">{s.label}</p>
                <p className="mt-1 text-xs text-slate-500">{s.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain / Problem */}
      <section className="bg-slate-50 py-24" aria-labelledby="pain-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="pain-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                The problem with managing {name} alone
              </h2>
              <ul className="mt-8 space-y-4">
                {painPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm">
                    <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <span className="text-slate-700">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900">The ZuriHub advantage</h3>
              <p className="mt-3 text-slate-600">{benefit}</p>
              <div className="mt-6 grid gap-3">
                {[
                  { label: 'Unified inbox', icon: MessageSquare },
                  { label: 'Customer context', icon: Users },
                  { label: 'AI assistance', icon: Zap },
                  { label: 'Analytics', icon: BarChart3 },
                ].map((x) => (
                  <div key={x.label} className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                    <x.icon className="h-5 w-5 text-brand-600" />
                    <span className="font-semibold text-slate-900">{x.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="how-heading" className="section-title">How the {name} integration works</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Connect {name} to ZuriHub in a few steps and start replying from one inbox.
            </p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {[
              { step: 1, title: 'Connect your account', text: `Link your ${name} account or business profile to ZuriHub.` },
              { step: 2, title: 'Route incoming messages', text: `Every ${name} message becomes a conversation in the unified queue.` },
              { step: 3, title: 'Reply with AI', text: 'Zyra AI suggests replies from your help docs and past answers.' },
              { step: 4, title: 'Track and improve', text: 'See response times, CSAT, and volume by channel in real time.' },
            ].map((h) => (
              <div key={h.step} className="relative flex gap-6">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-base font-bold text-white shadow-lg">
                  {h.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{h.title}</h3>
                  <p className="mt-2 text-slate-600">{h.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-slate-900 py-24 text-white" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="features-heading" className="section-title text-white">What you get</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl text-slate-400">
              Everything you need to manage {name} support at scale.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { title: 'How it works', text: how, icon: MousePointerClick },
              { title: 'The benefit', text: benefit, icon: Check },
              { title: 'Scalability', text: scalability, icon: Shield },
              { title: 'Pricing', text: pricing, icon: DollarSign },
            ].map((f, i) => {
              const FIcon = f.icon;
              return (
                <div
                  key={i}
                  className="rounded-3xl border border-slate-700 bg-slate-800 p-8 transition hover:-translate-y-1 hover:bg-slate-700"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                    <FIcon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-2 text-slate-300">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why ZuriHub */}
      <section className="py-24" aria-labelledby="why-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1 rounded-3xl border border-slate-200 bg-slate-50 p-8">
              <h3 className="text-2xl font-bold text-slate-900">Why teams choose ZuriHub for {name}</h3>
              <p className="mt-3 text-slate-600">
                Teams that connect {name} to ZuriHub respond faster, keep context, and scale without adding headcount.
              </p>
              <ul className="mt-6 space-y-3">
                {why.map((w) => (
                  <li key={w.title} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                    <div>
                      <span className="font-semibold text-slate-900">{w.title}</span>
                      <p className="text-sm text-slate-600">{w.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <h2 id="why-heading" className="section-title">Built for the way your customers already communicate</h2>
              <p className="mt-4 text-lg text-slate-600">
                Customers expect answers on the apps they use. ZuriHub brings {name} into the same queue as email, live chat, and every other channel — so no conversation is missed.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  { label: 'Faster response time', value: '-50%' },
                  { label: 'Happier customers', value: '+30% CSAT' },
                  { label: 'Less app switching', value: 'One inbox' },
                  { label: 'Lower cost', value: 'No per-message fees' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-2xl bg-slate-50 p-4 text-center">
                    <p className="text-2xl font-extrabold text-brand-600">{stat.value}</p>
                    <p className="text-sm font-semibold text-slate-700">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f, index) => (
              <div key={index} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div
          className="mx-auto max-w-5xl rounded-3xl px-6 py-16 text-center text-white sm:px-12"
          style={{ backgroundColor: color, backgroundImage: `linear-gradient(135deg, ${color} 0%, #0f172a 100%)` }}
        >
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start using {name} with ZuriHub</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-white/90">
            Connect your {name} account and manage every message from one inbox.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold transition hover:bg-slate-100"
            style={{ color }}
          >
            Connect {name} <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
